import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Check, ArrowLeft, Sparkles, Heart, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "free",
    name: "Free",
    subtitle: "Listening Mode",
    price: "$0",
    period: "",
    icon: Heart,
    features: [
      "5 voice vents",
      "Up to 2 minutes each",
      "Choose any persona",
      "Empathetic AI response",
      "Single-turn only (no interaction)",
    ],
    limitations: [
      "No Wednesday Vents",
      "No feedback",
    ],
    highlight: "Best for trying Ventaphy™",
    highlightColor: "text-muted-foreground",
    borderColor: "border-border",
    bgGradient: "",
  },
  {
    id: "basic",
    name: "Basic",
    subtitle: "Listening Mode+",
    price: "$4.99",
    period: "/month",
    icon: Sparkles,
    features: [
      "Unlimited voice or text vents",
      "Up to 3 minutes each",
      "Access to all personas",
      "Wednesday Vents access",
      "Empathetic AI response",
      "Single-turn only (listening only)",
    ],
    limitations: [
      "No feedback",
    ],
    highlight: "⭐ Most Popular",
    highlightColor: "text-primary",
    borderColor: "border-primary",
    bgGradient: "bg-gradient-to-br from-primary/5 to-transparent",
    popular: true,
  },
  {
    id: "premium",
    name: "Premium",
    subtitle: "Interactive Vent",
    price: "$9.99",
    period: "/month",
    icon: MessageCircle,
    features: [
      "Unlimited voice or text vents",
      "Up to 4 minutes each",
      "Access to all personas",
      "Interactive sessions (2–3 turns)",
      "Feedback on demand (only when you ask)",
      "Wednesday Vents + Extended sessions",
    ],
    limitations: [],
    highlight: "💜 Full experience, on your terms",
    highlightColor: "text-accent",
    borderColor: "border-accent/50",
    bgGradient: "bg-gradient-to-br from-accent/5 to-primary/5",
  },
];

export default function PricingPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleSelectPlan = async (planId) => {
    if (planId === "free") {
      if (user) {
        navigate("/persona");
      } else {
        navigate("/auth");
      }
      return;
    }

    if (!user) {
      toast.info("Please create an account first");
      navigate("/auth");
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { tier: planId },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            Origin: window.location.origin,
          },
          timeout: 15000 // 15 second timeout
        }
      );

      if (response.data.checkout_url) {
        // Store that we're in a checkout flow
        sessionStorage.setItem("ventaphy_checkout_pending", "true");
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      if (error.code === "ECONNABORTED") {
        toast.error("Request timed out. Please try again.");
      } else if (error.response?.status === 401) {
        toast.error("Please log in again to continue.");
        navigate("/auth");
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col"
      >
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl font-bold mb-2">
            Choose Your Plan
          </h1>
          <p className="text-muted-foreground">
            Everyone deserves a safe space to vent
          </p>
        </div>

        {/* Plans */}
        <div className="flex-1 space-y-4">
          {PLANS.map((plan, index) => {
            const IconComponent = plan.icon;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-3xl p-6 border ${plan.borderColor} ${plan.bgGradient || "bg-card"} ${
                  plan.popular ? "shadow-[0_0_20px_rgba(217,119,87,0.15)]" : ""
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      plan.popular ? "bg-primary/20" : "bg-muted"
                    }`}>
                      <IconComponent className={`w-5 h-5 ${plan.popular ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold">{plan.name}</h3>
                      <p className="text-xs text-muted-foreground">{plan.subtitle}</p>
                    </div>
                  </div>
                  
                  {user?.subscription_tier === plan.id && (
                    <span className="text-xs bg-secondary/20 text-secondary-foreground px-2 py-1 rounded-full">
                      Current
                    </span>
                  )}
                </div>

                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-primary">
                    {plan.price}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {plan.period}
                  </span>
                </div>

                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {plan.limitations.map((limitation) => (
                    <li key={limitation} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">—</span>
                      <span>{limitation}</span>
                    </li>
                  ))}
                </ul>

                <p className={`text-xs mb-4 ${plan.highlightColor}`}>
                  {plan.highlight}
                </p>

                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading === plan.id || user?.subscription_tier === plan.id}
                  className={`w-full h-12 rounded-full font-semibold ${
                    plan.popular
                      ? "bg-primary hover:bg-primary/90"
                      : plan.id === "premium"
                      ? "bg-gradient-to-r from-primary to-accent hover:opacity-90"
                      : "bg-muted hover:bg-muted/80 text-foreground"
                  }`}
                  data-testid={`select-${plan.id}-btn`}
                >
                  {loading === plan.id ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : user?.subscription_tier === plan.id ? (
                    "Current Plan"
                  ) : plan.id === "free" ? (
                    "Get Started"
                  ) : (
                    "Subscribe"
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-muted-foreground mt-6 pb-4"
        >
          Cancel anytime. No questions asked.
        </motion.p>
      </motion.div>
    </div>
  );
}
